import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { onServerWaking } from '../api';
import FrontierChart from './charts/FrontierChart';
import { parseHoldings, SAMPLE_HOLDINGS_TEXT } from '../utils/holdingsParser';
import { track } from '../utils/analytics';
import { formatNoteDate, usMarketStatus, MARKET_STATUS_LABEL } from '../utils/marketTime';
import { fmtMoney, fmtPct, fmtNum, signClass } from '../utils/format';
import { extractHoldingsFromImage, holdingsToText } from '../utils/ocrHoldings';
import './Landing.css';

// The public landing page. The hero is the product: paste holdings, get real
// numbers from /v1/try/analyze, then sign in to keep them.

const ILLUSTRATIVE = {
  frontier: [
    { risk: 9.8, return: 7.1 }, { risk: 10.6, return: 9.0 }, { risk: 11.6, return: 10.6 },
    { risk: 12.6, return: 12.4 }, { risk: 14.0, return: 13.6 }, { risk: 16.0, return: 14.8 },
    { risk: 18.4, return: 15.9 }, { risk: 21.0, return: 16.9 }, { risk: 24.0, return: 17.8 },
    { risk: 28.0, return: 18.6 },
  ],
  current: { risk: 14.9, return: 11.2 },
  maxSharpe: { risk: 12.6, return: 12.4 },
  minVol: { risk: 9.8, return: 7.1 },
};

const INDEX_NAMES = { '^GSPC': 'S&P 500', '^IXIC': 'NASDAQ', '^DJI': 'Dow', '^RUT': 'Russell 2000' };
const PLACEHOLDERS = [
  SAMPLE_HOLDINGS_TEXT,
  'AAPL 25, MSFT 10, VTI 40',
  'RELIANCE.NS 120, TCS.NS 30, INFY.NS 60',
  '100 x SPY, 20 x QQQ, 15 x BRK.B',
];

// A number that settles into place instead of appearing.
function CountUp({ value, format, duration = 700 }) {
  const [shown, setShown] = useState(value);
  const fromRef = useRef(0);
  useEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !Number.isFinite(value)) {
      setShown(value);
      return undefined;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - (1 - p) ** 3;
      setShown(from + (value - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{format(shown)}</>;
}

// Real index moves under the masthead, refreshed each minute while the tab is visible.
function MarketPulse() {
  const [indices, setIndices] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await api.get('/v1/market/summary', { skipRetry: true, timeout: 20000 });
        if (alive && res.data && res.data.indices && Object.keys(res.data.indices).length) setIndices(res.data.indices);
      } catch (e) {
        // strip stays hidden until the next poll
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  if (!indices) return null;
  const order = ['^GSPC', '^IXIC', '^DJI', '^RUT'].filter((k) => indices[k]);
  return (
    <div className="lp-pulse dd-mono" aria-label="Major indices">
      {order.map((k) => (
        <span key={k} className="lp-pulse-item">
          <span className="lp-pulse-name">{INDEX_NAMES[k] || k}</span>
          <span className="dd-num">{Number(indices[k].price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <span className={`dd-num ${signClass(indices[k].percent_change)}`}>{fmtPct(indices[k].percent_change, { digits: 2 })}</span>
        </span>
      ))}
    </div>
  );
}

const METRICS = [
  ['sharpe_ratio', 'Sharpe', 'ratio'],
  ['sortino_ratio', 'Sortino', 'ratio'],
  ['var_95', 'VaR 95 · one day', 'pct'],
  ['cvar_95', 'Expected shortfall', 'pct'],
  ['max_drawdown', 'Max drawdown', 'pct'],
  ['beta', 'Beta', 'ratio'],
];

const GoogleMark = () => (
  <svg className="lp-g" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

function metricDisplay(key, kind, metric, base) {
  const v = metric?.value;
  if (v == null) return { num: null, format: () => '—', sub: '' };
  if (kind === 'ratio') return { num: v, format: (n) => fmtNum(n, 2), sub: metric.rating ? String(metric.rating) : '' };
  let sub = '';
  if (key === 'max_drawdown') {
    sub = metric.recovery_days != null ? `recovered in ${metric.recovery_days} days` : 'not yet recovered';
  } else if (metric.dollar_amount != null) {
    sub = `${fmtMoney(Math.abs(metric.dollar_amount), base)} on a bad day`;
  } else if (metric.rating) {
    sub = String(metric.rating);
  }
  return { num: v * 100, format: (n) => fmtPct(n, { digits: 1, sign: n < 0 }), sub };
}

function ResultPanel({ result, onSignIn, signingIn, onReset, onBaseChange, switching }) {
  const holdings = result.holdings || [];
  const base = result.base_currency || 'USD';
  const q = result.quant || {};
  const f = result.frontier;
  const failed = q.data_coverage?.failed_tickers || [];
  const fx = q.data_coverage?.fx_converted || [];
  const days = q.data_coverage?.trading_days_analyzed;
  const pill = (h) => `${h.ticker} ${q.weights?.[h.ticker] != null ? `${q.weights[h.ticker].toFixed(1)}%` : ''}`.trim();

  return (
    <div className="lp-result">
      <div className="lp-result-head">
        <div>
          <div className="dd-eyebrow">Your holdings, analysed</div>
          <div className="lp-result-tickers dd-mono">{holdings.map(pill).join(' · ')}</div>
        </div>
        {q.portfolio_value != null && (
          <div className="lp-result-value">
            <span className="dd-serif dd-num"><CountUp value={Number(q.portfolio_value)} format={(n) => fmtMoney(n, base)} /></span>
            <span className="lp-base-toggle dd-mono" role="group" aria-label="Currency">
              {['USD', 'INR'].map((c) => (
                <button key={c} type="button" className={c === base ? 'on' : ''} onClick={() => c !== base && onBaseChange(c)} disabled={switching}>
                  {c === 'USD' ? '$ USD' : '₹ INR'}
                </button>
              ))}
            </span>
          </div>
        )}
      </div>

      <div className="lp-metrics">
        {METRICS.map(([key, label, kind]) => {
          const m = q.metrics?.[key];
          const { num, format, sub } = metricDisplay(key, kind, m, base);
          const cls = kind === 'pct' && m?.value < 0 ? 'dd-loss' : '';
          return (
            <div key={key} className="lp-metric">
              <div className="dd-eyebrow">{label}</div>
              <div className={`lp-metric-value dd-serif dd-num ${cls}`}>{num == null ? '—' : <CountUp value={num} format={format} />}</div>
              {sub && <div className="lp-metric-sub dd-muted">{sub}</div>}
            </div>
          );
        })}
      </div>

      {f ? (
        <FrontierChart
          frontier={f.efficient_frontier}
          current={f.current_portfolio}
          maxSharpe={f.max_sharpe}
          minVol={f.min_volatility}
          height={260}
          caption={`Markowitz, long-only, ${days || 252} trading days. The dashed line is the move from where you are to the best return per unit of risk.`}
        />
      ) : (
        <p className="lp-result-note dd-muted">{result.frontier_error || 'Add a second holding to see the efficient frontier.'}</p>
      )}

      {failed.length > 0 && (
        <p className="lp-result-note dd-muted">No price history for {failed.join(', ')}; they were left out.</p>
      )}
      {fx.length > 0 && (
        <p className="lp-result-note dd-muted">{fx.join(', ')} converted to {base} at each day's rate.</p>
      )}

      <div className="lp-result-cta">
        <button type="button" className="dd-btn lp-btn-lg" onClick={onSignIn} disabled={signingIn}>
          <GoogleMark />
          {signingIn ? 'Signing in…' : 'Sign in with Google to keep this note'}
        </button>
        <button type="button" className="lp-link-btn" onClick={onReset}>Analyse different holdings</button>
      </div>
      <p className="lp-result-note dd-muted">Signing in adds earnings dates, news read against your names, the optimiser, and the analyst you can question.</p>
    </div>
  );
}

export default function Landing({ onSignIn, signingIn, sessionExpired, onDismissExpired }) {
  const [text, setText] = useState('');
  const [errors, setErrors] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [waking, setWaking] = useState(false);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [base, setBase] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [ocr, setOcr] = useState(null);
  const heroRef = useRef(null);

  useEffect(() => {
    if (text) return undefined;
    const timer = setInterval(() => setPlaceholderIdx((i) => (i + 1) % PLACEHOLDERS.length), 3500);
    return () => clearInterval(timer);
  }, [text]);

  const readScreenshot = useCallback(async (file) => {
    if (!file) return;
    setOcr({ busy: true, note: 'Reading the screenshot on this device…' });
    try {
      const { holdings } = await extractHoldingsFromImage(file, (p) => setOcr({ busy: true, note: `Reading… ${Math.round(p * 100)}%` }));
      if (!holdings.length) {
        setOcr({ busy: false, note: 'No tickers with share counts were found in that image. Try a tighter crop of the holdings table.' });
        return;
      }
      setText(holdingsToText(holdings));
      setErrors([]);
      setOcr({ busy: false, note: `Read ${holdings.length} holdings from the screenshot. Nothing was uploaded. Check them, then analyse.` });
      track('try_screenshot', { holdings: holdings.length });
    } catch (err) {
      setOcr({ busy: false, note: err.message || 'Could not read that image.' });
    }
  }, []);

  useEffect(() => onServerWaking(setWaking), []);

  // Reveal sections as they scroll into view; reduced-motion users see them immediately via CSS.
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('.lp-reveal'));
    if (!nodes.length || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('in'));
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  const runAnalysis = useCallback(async (raw, baseCurrency = null) => {
    const { holdings, errors: parseErrors } = parseHoldings(raw);
    if (parseErrors.length) {
      setErrors(parseErrors);
      return;
    }
    if (!holdings.length) {
      setErrors(['Add at least one holding, like "VTI 120".']);
      return;
    }
    setErrors([]);
    setFailure(null);
    setAnalyzing(true);
    try {
      const res = await api.post('/v1/try/analyze', baseCurrency ? { holdings, base_currency: baseCurrency } : { holdings });
      setResult(res.data);
      track('try_analyze', { holdings: holdings.length, sample: raw === SAMPLE_HOLDINGS_TEXT, base: res.data?.base_currency || 'USD' });
      if (heroRef.current && heroRef.current.scrollIntoView) {
        heroRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status === 429) {
        msg = detail?.message || 'Too many analyses from this connection. Try again in a minute.';
      } else if (status === 400) {
        msg = detail?.message || 'Could not analyse those holdings.';
        if (detail?.failed_tickers?.length) msg += ` No price history for ${detail.failed_tickers.join(', ')}.`;
      } else if (status === 422) {
        msg = 'One of those tickers or share counts is not valid.';
      } else {
        msg = 'The server did not answer. It may be waking up on free hosting; try again in a moment.';
      }
      setFailure(msg);
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    runAnalysis(text, base);
  };

  const changeBase = async (next) => {
    setBase(next);
    setSwitching(true);
    try {
      await runAnalysis(text || SAMPLE_HOLDINGS_TEXT, next);
    } finally {
      setSwitching(false);
    }
  };

  const loadSample = () => {
    setText(SAMPLE_HOLDINGS_TEXT);
    runAnalysis(SAMPLE_HOLDINGS_TEXT, base);
  };

  const status = usMarketStatus();

  return (
    <div className="lp">
      {sessionExpired && (
        <div className="lp-banner" role="status">
          <span>Your session expired. Sign in again to pick up where you left off.</span>
          <button type="button" className="lp-link-btn" onClick={onDismissExpired}>Dismiss</button>
        </div>
      )}

      <header className="lp-masthead">
        <a href="#top" className="lp-brand">
          <img src="/icons/icon-128.webp" alt="" width="32" height="32" />
          <span className="dd-serif">DeepDime</span>
        </a>
        <div className="lp-dateline dd-mono"><span className={`lp-dot lp-dot--${status}`} aria-hidden="true" />Research note · {formatNoteDate()} · {MARKET_STATUS_LABEL[status]}</div>
        <nav className="lp-masthead-nav">
          <a href="#methods">Methodology</a>
          <a href="#pricing">Pricing</a>
          <button type="button" className="lp-signin" onClick={onSignIn} disabled={signingIn}>
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </nav>
      </header>
      <MarketPulse />

      <section className="lp-hero" id="top" ref={heroRef}>
        <div className="lp-hero-copy">
          <div className="dd-eyebrow lp-rise">Free · No account needed to start</div>
          <h1 className="dd-serif lp-rise lp-d1">The research note your portfolio never got.</h1>
          <p className="lp-lede lp-rise lp-d2">Paste what you hold. Get Sharpe, drawdown, value-at-risk and an optimised allocation in seconds, written up like a desk analyst would.</p>

          <form className="lp-form lp-rise lp-d3" onSubmit={handleSubmit}>
            <label htmlFor="lp-holdings" className="dd-eyebrow lp-label">Your holdings</label>
            <textarea
              id="lp-holdings"
              rows={3}
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDERS[placeholderIdx]}
              aria-describedby="lp-holdings-hint"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); readScreenshot(e.dataTransfer.files && e.dataTransfer.files[0]); }}
            />
            <div className="lp-form-row">
              <button type="submit" className="dd-btn lp-btn-lg" disabled={analyzing}>
                {analyzing ? (waking ? 'Waking the server…' : 'Analysing…') : 'Analyse my holdings'}
                {!analyzing && <Arrow />}
              </button>
              <button type="button" className="lp-link-btn" onClick={loadSample} disabled={analyzing}>or load a sample portfolio</button>
              <label className={`lp-link-btn lp-file ${ocr?.busy ? 'busy' : ''}`}>
                or read a screenshot
                <input type="file" accept="image/*" hidden disabled={ocr?.busy} onChange={(e) => { readScreenshot(e.target.files && e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
            <div id="lp-holdings-hint" className="lp-hint dd-mono dd-muted">Tickers and share counts, US or NSE. Paste, type, or drop a screenshot of your holdings. Nothing is stored until you sign in.</div>
            {ocr?.note && <div className={`lp-hint dd-mono ${ocr.busy ? 'dd-muted' : ''}`} role="status">{ocr.note}</div>}
            {errors.length > 0 && (
              <ul className="lp-errors" role="alert">
                {errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
            {failure && <p className="lp-errors" role="alert">{failure}</p>}
          </form>
        </div>

        <div className="lp-hero-visual">
          {result ? (
            <ResultPanel result={result} onSignIn={onSignIn} signingIn={signingIn} onReset={() => { setResult(null); setText(''); }} onBaseChange={changeBase} switching={switching || analyzing} />
          ) : (
            <div className="lp-illustration">
              <FrontierChart
                frontier={ILLUSTRATIVE.frontier}
                current={ILLUSTRATIVE.current}
                maxSharpe={ILLUSTRATIVE.maxSharpe}
                minVol={ILLUSTRATIVE.minVol}
                height={420}
                animate
                caption="Illustrative frontier. The marker traces the move the optimiser proposes. Paste your holdings above and this becomes yours, computed from a year of real prices."
              />
            </div>
          )}
        </div>
      </section>

      <hr className="lp-rule" />

      <section className="lp-section lp-two lp-reveal">
        <div className="lp-section-copy">
          <div className="dd-eyebrow">The problem</div>
          <h2 className="dd-serif">A chatbot knows the stock. It doesn't know your portfolio.</h2>
          <p className="dd-muted">Ask any assistant whether to hold a share and you get a paragraph that would be true for anyone. The number that matters is what that share is doing to <em>your</em> risk.</p>
        </div>
        <div className="lp-compare">
          <div className="lp-card lp-card--flat">
            <div className="dd-eyebrow">A general assistant</div>
            <div className="lp-q dd-mono">“is TTWO a long term hold?”</div>
            <p className="dd-muted">Take-Two Interactive could be a solid long-term holding given its strong franchises such as Grand Theft Auto and NBA 2K. However, investing carries risk and you should consider your own financial situation, diversification, and consult a licensed advisor before making any decisions…</p>
          </div>
          <div className="lp-card lp-card--ink">
            <div className="dd-eyebrow lp-eyebrow-ink">DeepDime, on your holdings</div>
            <div className="lp-q dd-mono">“is TTWO a long term hold?”</div>
            <p className="dd-serif lp-verdict">Hold, but trim. At 15.6% it is your fourth-largest position and 3.6% under water. A typical ±10% move on the 4 Nov print is about $1,300 either way.</p>
            <table className="lp-table dd-num">
              <tbody>
                <tr><td className="dd-muted">Weight</td><td className="r">15.6% · #4 of 5</td></tr>
                <tr><td className="dd-muted">Since cost</td><td className="r dd-loss">−3.6% · −$490</td></tr>
                <tr><td className="dd-muted">Earnings</td><td className="r">4 Nov · confirmed</td></tr>
                <tr><td className="dd-muted">Beta</td><td className="r">0.92</td></tr>
              </tbody>
            </table>
            <div className="lp-sample dd-mono dd-muted">Sample portfolio</div>
          </div>
        </div>
      </section>

      <hr className="lp-rule" />

      <section className="lp-section lp-two lp-two--rev lp-reveal">
        <div className="lp-note">
          <div className="lp-note-head">
            <div className="dd-eyebrow">Portfolio note · sample</div>
            <div className="dd-mono dd-muted">5 holdings · USD</div>
          </div>
          <div className="lp-note-value">
            <span className="dd-serif dd-num">$84,310</span>
            <span className="dd-num dd-gain">+$612 today · +0.73%</span>
          </div>
          <div className="lp-note-grid">
            <div><div className="dd-eyebrow">Sharpe</div><div className="dd-serif dd-num lp-note-num">1.12</div></div>
            <div className="lp-callout"><div className="dd-eyebrow">VaR 95 · 1 day</div><div className="dd-serif dd-num lp-note-num dd-loss">−1.9%</div><div className="dd-mono dd-muted">−$1,602</div><span className="lp-num">1</span></div>
            <div><div className="dd-eyebrow">Max drawdown</div><div className="dd-serif dd-num lp-note-num dd-loss">−14.2%</div><div className="dd-mono dd-muted">recovered in 31 days</div></div>
            <div><div className="dd-eyebrow">Beta</div><div className="dd-serif dd-num lp-note-num">1.08</div></div>
          </div>
          <div className="lp-note-cols">
            <div className="lp-callout">
              <div className="dd-eyebrow">Where the frontier says you could be</div>
              <p>Moving 9 points of VTI into the max-Sharpe mix lifts expected return from 11.2% to 12.4% and cuts volatility from 14.9% to 12.6%. Same journey, smaller drawdown.</p>
              <span className="lp-num">2</span>
            </div>
            <div className="lp-callout">
              <div className="dd-eyebrow">What changed</div>
              <div className="dd-mono lp-changes">NVDA +2.1% today<br />TTWO earnings confirmed 4 Nov<br />VaR widened −1.7% → −1.9%</div>
              <span className="lp-num">3</span>
            </div>
          </div>
        </div>
        <div className="lp-section-copy">
          <div className="dd-eyebrow">What you get</div>
          <h2 className="dd-serif">One page, three answers, every morning.</h2>
          <ol className="lp-steps">
            <li><span className="lp-num lp-num--inline">1</span><div><strong>How much you can lose on a bad day.</strong><div className="dd-muted">Value-at-risk and expected shortfall, in dollars, not just percentages.</div></div></li>
            <li><span className="lp-num lp-num--inline">2</span><div><strong>Whether you are being paid for that risk.</strong><div className="dd-muted">Your position against the efficient frontier, and the allocation that closes the gap.</div></div></li>
            <li><span className="lp-num lp-num--inline">3</span><div><strong>What changed since yesterday.</strong><div className="dd-muted">Prices, earnings dates, and risk, only for the names you hold.</div></div></li>
          </ol>
        </div>
      </section>

      <hr className="lp-rule" />

      <section className="lp-section lp-two lp-reveal" id="methods">
        <div className="lp-section-copy">
          <div className="dd-eyebrow">How it is computed</div>
          <h2 className="dd-serif">The math writes the numbers. The AI only writes the prose.</h2>
          <p className="dd-muted">Every figure on the note comes from published formulas over real price history. The language model is handed those figures and told not to invent any.</p>
        </div>
        <table className="lp-table lp-table--methods">
          <thead><tr><th>Figure</th><th>Method</th><th className="r">Inputs</th></tr></thead>
          <tbody>
            <tr><td>Sharpe, Sortino</td><td className="dd-muted">Annualised excess return over deviation; Sortino uses downside semi-deviation.</td><td className="r dd-mono">252 days · rf 4.25%</td></tr>
            <tr><td>Value-at-risk, expected shortfall</td><td className="dd-muted">Historical, 95% confidence, one-day horizon, scaled to your dollar value.</td><td className="r dd-mono">daily returns</td></tr>
            <tr><td>Efficient frontier</td><td className="dd-muted">Markowitz long-only; Ledoit–Wolf shrinkage and Black–Litterman views available.</td><td className="r dd-mono">30 points · SLSQP</td></tr>
            <tr><td>Beta, drawdown</td><td className="dd-muted">Against the S&amp;P 500; peak-to-trough on cumulative return with recovery days.</td><td className="r dd-mono">^GSPC</td></tr>
            <tr><td>Prices, earnings</td><td className="dd-muted">Live quotes and confirmed earnings dates; estimated dates are labelled as such.</td><td className="r dd-mono">Finnhub · Yahoo</td></tr>
          </tbody>
        </table>
      </section>

      <hr className="lp-rule" />

      <section className="lp-section lp-two lp-reveal" id="pricing">
        <div className="lp-section-copy">
          <div className="dd-eyebrow">Free to start</div>
          <h2 className="dd-serif">Analyse for free. Pay when you want the optimiser every day.</h2>
          <div className="lp-form-row">
            <button type="button" className="dd-btn lp-btn-lg" onClick={onSignIn} disabled={signingIn}>
              <GoogleMark />
              {signingIn ? 'Signing in…' : 'Sign in with Google'}
            </button>
            <a href="#top" className="lp-link-btn">Try it first</a>
          </div>
        </div>
        <table className="lp-table lp-table--pricing dd-num">
          <thead><tr><th>Included</th><th className="r">Free</th><th className="r">Pro · $9.99/mo</th></tr></thead>
          <tbody>
            <tr><td>Portfolio note, Sharpe, beta</td><td className="r">Yes</td><td className="r">Yes</td></tr>
            <tr><td>VaR, drawdown, Sortino</td><td className="r dd-muted">Locked</td><td className="r">Yes</td></tr>
            <tr><td>Optimiser runs</td><td className="r">5 / day</td><td className="r">50 / day</td></tr>
            <tr><td>Questions to the analyst</td><td className="r">10 / day</td><td className="r">100 / day</td></tr>
            <tr><td>News read against your holdings</td><td className="r dd-muted">—</td><td className="r">Yes</td></tr>
          </tbody>
        </table>
      </section>

      <footer className="lp-footer dd-mono">
        <div className="dd-muted">DeepDime · Analysis, not financial advice. Figures can be wrong; verify before acting.</div>
        <div className="lp-footer-links"><a href="#methods">Methodology</a><a href="/premium">Pricing</a><a href="mailto:hello@deepaifinance.com">Contact</a></div>
      </footer>
    </div>
  );
}
